import { SortingHeaderButton } from '@/components/ui/DataTable/SortingHeaderButton'
import { RestSchemas } from '@/contexts/QueryProvider'
import { ColumnDef } from '@tanstack/react-table'
import clsx from 'clsx'
import { IconAvatarOutline } from '@icons/IconAvatarOutline'
import { IconCommunityOutline } from '@icons/IconCommunityOutline'
import { NumberBadge } from '@/components/ui/DataTable/NumberBadge'
import { IconAccountConvertOutline } from '@icons/IconAccountConvertOutline'
import { RowActionsMenu } from './RowActionsMenu'
import { ApplicationIcon, ProjectIcon } from '@/const'

type MessagesSubscriptionsOrganisationTeam = RestSchemas['Messages.Subscriptions.OrganisationTeam']

export const columns: ColumnDef<MessagesSubscriptionsOrganisationTeam>[] = [
  {
    id: 'team',
    accessorFn: (row) => clsx(row.name, row.description),
    header: ({ column }) => <SortingHeaderButton column={column}>Name</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="group flex items-start gap-x-4">
        <div className={clsx('flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10')}>
          <IconCommunityOutline className={clsx('size-5 text-primary')} />
        </div>
        <div className="flex flex-col gap-y-1">
          <span className="text-base">{row.original.name}</span>
          <span className="text-xs font-light leading-[1.1] text-muted-foreground">{row.original.description}</span>
        </div>
      </div>
    ),
    sortingFn: (rowA, rowB) => rowA.original.name.localeCompare(rowB.original.name),
  },
  {
    id: 'application',
    accessorKey: 'application',
    header: ({ column }) => <SortingHeaderButton column={column}>Application</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="group flex items-center gap-x-4">
        <div
          className={clsx(
            'flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground'
          )}
        >
          <ApplicationIcon className={clsx('size-5')} />
        </div>
        <span className="text-base">{row.original.application}</span>
      </div>
    ),
    sortingFn: (rowA, rowB) => (rowA.original.application || '').localeCompare(rowB.original.application || ''),
  },
  {
    id: 'projects',
    header: ({ column }) => <SortingHeaderButton column={column}>Projects</SortingHeaderButton>,
    accessorKey: 'linkedProjectCount',
    cell: ({ row }) => (
      <div className="group flex items-center gap-x-4">
        <ProjectIcon className={clsx('size-5 text-muted-foreground')} />
        <div className="flex items-center gap-x-2">
          <span className="flex items-center">
            <NumberBadge value={row.original.linkedProjectCount} />
            <span className="text-xs italic text-muted-foreground">-Total</span>
          </span>
          <span className="text-border">|</span>
          <span className="flex items-center">
            <NumberBadge value={row.original.activeLinkedProjectCount} />
            <span className="text-xs italic text-muted-foreground">-Active</span>
          </span>
        </div>
      </div>
    ),
    sortingFn: (rowA, rowB) => rowA.original.linkedProjectCount - rowB.original.linkedProjectCount,
  },
  {
    id: 'roles',
    header: ({ column }) => <SortingHeaderButton column={column}>Roles</SortingHeaderButton>,
    accessorKey: 'linkedRoleCount',
    cell: ({ row }) => (
      <div className="group flex items-center gap-x-4">
        <IconAccountConvertOutline className={clsx('size-5 text-muted-foreground')} />
        <NumberBadge value={row.original.linkedRoleCount} />
      </div>
    ),
  },
  {
    id: 'users',
    header: ({ column }) => <SortingHeaderButton column={column}>Users</SortingHeaderButton>,
    accessorKey: 'linkedUserCount',
    cell: ({ row }) => (
      <div className="group flex items-center gap-x-4">
        <IconAvatarOutline className={clsx('size-5 text-muted-foreground')} />
        <NumberBadge value={row.original.linkedUserCount} />
      </div>
    ),
  },
  {
    id: 'actions',
    // header: 'Actions',
    cell: ({ row }) => (
      <div className="flex justify-end pr-5">
        <RowActionsMenu team={row.original} />
      </div>
    ),
  },
]
